import React, { useState, Fragment } from 'react';
import { Users, AlertCircle, CheckCircle2, HelpCircle, ChevronDown, ChevronUp, Search, AlertTriangle } from 'lucide-react';
import { dashboardData } from './data';
import { motion, AnimatePresence } from 'framer-motion';
export function LoBreakdownTable() {
  const {
    loBreakdown
  } = dashboardData;
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [expandedLoId, setExpandedLoId] = useState<string | null>(null);
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'On Track':
        return <CheckCircle2 size={16} className="text-green-500" data-id="element-141" />;
      case 'Needs Attention':
        return <AlertCircle size={16} className="text-orange-500" data-id="element-142" />;
      case 'New':
        return <HelpCircle size={16} className="text-blue-400" data-id="element-143" />;
      default:
        return null;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'On Track':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Needs Attention':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'New':
        return 'bg-slate-50 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };
  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Credit Pull':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Doc Out':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'Submission':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'Closing':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Funded':
        return 'text-slate-600 bg-slate-50 border-slate-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };
  const toggleExpand = (id: string) => {
    setExpandedLoId(expandedLoId === id ? null : id);
  };
  const filteredLOs = loBreakdown.filter(lo => {
    const matchesSearch = lo.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || lo.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  return <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-144">
      <div className="px-6 py-5 border-b border-slate-200 bg-white" data-id="element-145">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-146">
          <div className="flex items-center gap-3" data-id="element-147">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-148">
              <Users size={20} data-id="element-149" />
            </div>
            <div data-id="element-150">
              <h2 className="text-base font-bold text-[#0a1e5e] uppercase tracking-wide leading-tight" data-id="element-151">
                LO Breakdown
              </h2>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-152">
                Identify how to best assist your team
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-153">
            <div className="relative flex-1 lg:w-64" data-id="element-154">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-155" />
              <input type="text" placeholder="Search LO..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-9 pl-9 pr-4 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-156" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 appearance-none pl-4 pr-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-slate-700 cursor-pointer" data-id="element-157">
              <option value="All" data-id="element-158">All Statuses</option>
              <option value="On Track" data-id="element-159">On Track</option>
              <option value="Needs Attention" data-id="element-160">Needs Attention</option>
              <option value="New" data-id="element-161">New</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-162">
        <table className="w-full text-sm text-left" data-id="element-163">
          <thead className="text-xs text-slate-500 uppercase bg-white border-b border-slate-200" data-id="element-164">
            <tr data-id="element-165">
              <th className="px-6 py-4 font-bold" data-id="element-166">LO Name</th>
              <th className="px-4 py-4 font-bold text-center" data-id="element-167">
                <div className="text-blue-600" data-id="element-168">Credit Pulls</div>
                <div className="text-[10px] font-normal mt-0.5" data-id="element-169">(750 PTS)</div>
              </th>
              <th className="px-4 py-4 font-bold text-center" data-id="element-170">
                <div className="text-green-600" data-id="element-171">Doc Outs</div>
                <div className="text-[10px] font-normal mt-0.5" data-id="element-172">(500 PTS)</div>
              </th>
              <th className="px-4 py-4 font-bold text-center" data-id="element-173">
                <div className="text-purple-600" data-id="element-174">Submissions</div>
                <div className="text-[10px] font-normal mt-0.5" data-id="element-175">(500 PTS)</div>
              </th>
              <th className="px-4 py-4 font-bold text-center" data-id="element-176">
                <div className="text-orange-500" data-id="element-177">Closings</div>
                <div className="text-[10px] font-normal mt-0.5" data-id="element-178">(250 PTS)</div>
              </th>
              <th className="px-4 py-4 font-bold text-center" data-id="element-179">Total Points</th>
              <th className="px-4 py-4 font-bold text-center" data-id="element-180">
                <div data-id="element-181">% of LO Goal</div>
                <div className="text-[10px] font-normal mt-0.5" data-id="element-182">
                  (10,000 PTS)
                </div>
              </th>
              <th className="px-6 py-4 font-bold text-right" data-id="element-183">Status</th>
            </tr>
          </thead>
          <tbody data-id="element-184">
            {filteredLOs.length === 0 ? <tr data-id="element-185">
                <td colSpan={8} className="px-6 py-8 text-center text-slate-500" data-id="element-186">
                  No Loan Officers found matching your filters.
                </td>
              </tr> : filteredLOs.map((lo, index) => <Fragment key={lo.id} data-id="element-187">
                  <tr onClick={() => toggleExpand(lo.id)} className={`border-b border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'} ${expandedLoId === lo.id ? 'bg-blue-50/30' : ''}`} data-id="element-188">
                    <td className="px-6 py-4" data-id="element-189">
                      <div className="flex items-center gap-3" data-id="element-190">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 ${lo.status === 'New' ? 'bg-slate-300' : 'bg-[#0a1e5e]'}`} data-id="element-191">
                          {lo.initials}
                        </div>
                        <div data-id="element-192">
                          <p className="font-bold text-slate-800 flex items-center gap-2" data-id="element-193">
                            {lo.name}
                            {lo.files && lo.files.some(f => f.isNotMoving) && <span className="flex items-center justify-center w-4 h-4 bg-red-100 rounded-full" title="Has files not moving" data-id="element-194">
                                  <AlertTriangle size={10} className="text-red-600" data-id="element-195" />
                                </span>}
                          </p>
                          {lo.status === 'New' && <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-196">
                              (NEW)
                            </p>}
                        </div>
                      </div>
                    </td>

                    {lo.status === 'New' ? <>
                        <td className="px-4 py-4 text-center text-slate-300 font-bold" data-id="element-197">
                          —
                        </td>
                        <td className="px-4 py-4 text-center text-slate-300 font-bold" data-id="element-198">
                          —
                        </td>
                        <td className="px-4 py-4 text-center text-slate-300 font-bold" data-id="element-199">
                          —
                        </td>
                        <td className="px-4 py-4 text-center text-slate-300 font-bold" data-id="element-200">
                          —
                        </td>
                        <td className="px-4 py-4 text-center text-slate-300 font-bold" data-id="element-201">
                          —
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-202">
                          <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-203">
                            NEW LO
                            <br data-id="element-204" />
                            (NOT COUNTED)
                          </span>
                        </td>
                      </> : <>
                        <td className="px-4 py-4 text-center" data-id="element-205">
                          <div className="font-bold text-slate-800" data-id="element-206">
                            {lo.creditPulls.count}
                          </div>
                          <div className="text-xs text-blue-600 font-medium" data-id="element-207">
                            ({lo.creditPulls.points?.toLocaleString()})
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-208">
                          <div className="font-bold text-slate-800" data-id="element-209">
                            {lo.docOuts.count}
                          </div>
                          <div className="text-xs text-green-600 font-medium" data-id="element-210">
                            ({lo.docOuts.points?.toLocaleString()})
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-211">
                          <div className="font-bold text-slate-800" data-id="element-212">
                            {lo.submissions.count}
                          </div>
                          <div className="text-xs text-purple-600 font-medium" data-id="element-213">
                            ({lo.submissions.points?.toLocaleString()})
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-214">
                          <div className="font-bold text-slate-800" data-id="element-215">
                            {lo.closings.count}
                          </div>
                          <div className="text-xs text-orange-500 font-medium" data-id="element-216">
                            ({lo.closings.points?.toLocaleString()})
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-217">
                          <div className="font-black text-[#0a1e5e] text-base" data-id="element-218">
                            {lo.totalPoints?.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center" data-id="element-219">
                          <div className="font-bold text-green-600" data-id="element-220">
                            {lo.percentOfGoal?.toFixed(1)}%
                          </div>
                        </td>
                      </>}

                    <td className="px-6 py-4 text-right" data-id="element-221">
                      <div className="flex items-center justify-end gap-3" data-id="element-222">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusBadge(lo.status)}`} data-id="element-223">
                          {getStatusIcon(lo.status)}
                          {lo.status}
                        </span>
                        <div className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors" data-id="element-224">
                          {expandedLoId === lo.id ? <ChevronUp size={18} data-id="element-225" /> : <ChevronDown size={18} data-id="element-226" />}
                        </div>
                      </div>
                    </td>
                  </tr>

                  <AnimatePresence data-id="element-227">
                    {expandedLoId === lo.id && <tr className="bg-slate-50 border-b border-slate-200" data-id="element-228">
                        <td colSpan={8} className="p-0" data-id="element-229">
                          <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden" data-id="element-230">
                            <div className="p-6" data-id="element-231">
                              <div className="flex justify-between items-center mb-4" data-id="element-232">
                                <h4 className="text-sm font-bold text-[#0a1e5e] uppercase tracking-wide" data-id="element-233">
                                  Active Files ({lo.files?.length || 0})
                                </h4>
                                {lo.status === 'Needs Attention' && <button className="text-xs font-bold bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors uppercase tracking-wider" data-id="element-234">
                                    Coach LO
                                  </button>}
                              </div>

                              {lo.files && lo.files.length > 0 ? <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-235">
                                  <table className="w-full text-sm text-left" data-id="element-236">
                                    <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-237">
                                      <tr data-id="element-238">
                                        <th className="px-4 py-3 font-bold" data-id="element-239">
                                          Borrower
                                        </th>
                                        <th className="px-4 py-3 font-bold" data-id="element-240">
                                          Loan Amount
                                        </th>
                                        <th className="px-4 py-3 font-bold" data-id="element-241">
                                          Type
                                        </th>
                                        <th className="px-4 py-3 font-bold" data-id="element-242">
                                          Stage
                                        </th>
                                        <th className="px-4 py-3 font-bold text-right" data-id="element-243">
                                          Days in Stage
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-244">
                                      {lo.files.map(file => <tr key={file.id} className={`border-b border-slate-200 last:border-0 hover:bg-slate-50 ${file.isNotMoving ? 'bg-red-50/30' : ''}`} data-id="element-245">
                                          <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2" data-id="element-246">
                                            {file.borrowerName}
                                            {file.isNotMoving && <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded uppercase" data-id="element-247">
                                                <AlertTriangle size={10} data-id="element-248" /> Not
                                                Moving
                                              </span>}
                                          </td>
                                          <td className="px-4 py-3 text-slate-600" data-id="element-249">
                                            ${file.loanAmount.toLocaleString()}
                                          </td>
                                          <td className="px-4 py-3 text-slate-600" data-id="element-250">
                                            {file.loanType}
                                          </td>
                                          <td className="px-4 py-3" data-id="element-251">
                                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${getStageColor(file.stage)}`} data-id="element-252">
                                              {file.stage}
                                            </span>
                                          </td>
                                          <td className={`px-4 py-3 text-right font-bold ${file.isNotMoving ? 'text-red-600' : 'text-slate-600'}`} data-id="element-253">
                                            {file.daysInStage}
                                          </td>
                                        </tr>)}
                                    </tbody>
                                  </table>
                                </div> : <div className="text-center py-6 bg-white rounded-xl border border-slate-200 text-slate-500 text-sm" data-id="element-254">
                                  No active files found for this Loan Officer.
                                </div>}
                            </div>
                          </motion.div>
                        </td>
                      </tr>}
                  </AnimatePresence>
                </Fragment>)}
          </tbody>
        </table>
      </div>
    </div>;
}